import ShowLoader from '@/components/Loader';
import Toaster from '@/components/Toaster';
import {
  completeRegistration,
  resendOTP,
  verifyOTP,
} from '@/store/slices/deliveryUserSlice';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

export function OTPVerification({
  isOpen,
  onClose,
  onSuccess,
  registrationFormData,
}) {
  const { t } = useTranslation('signup');

  const dispatch = useDispatch();

  const [mobileTimer, setMobileTimer] = useState(30);
  const [emailTimer, setEmailTimer] = useState(30);

  const [mobileOtp, setMobileOtp] = useState(new Array(4).fill(''));
  const [emailOtp, setEmailOtp] = useState(new Array(4).fill(''));

  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const { loading } = useSelector((state) => state.delivery_user);

  const mobileRefs = useRef([]);
  const emailRefs = useRef([]);

  /*
   * Get verification status
   */
  const isMobileVerified =
    localStorage.getItem('is_mobile_verified') === 'true';

  const isEmailVerified =
    localStorage.getItem('is_email_verified') === 'true';

  /*
   * OTP input change
   */
  const handleChange = (element, index, type) => {
    if (isNaN(element.value)) return false;

    const otpArray =
      type === 'mobile' ? [...mobileOtp] : [...emailOtp];

    const refs =
      type === 'mobile' ? mobileRefs : emailRefs;

    const setter =
      type === 'mobile' ? setMobileOtp : setEmailOtp;

    otpArray[index] = element.value;

    setter(otpArray);

    // Focus next input
    if (element.value !== '' && index < 3) {
      refs.current[index + 1]?.focus();
    }
  };

  /*
   * Backspace handling
   */
  const handleKeyDown = (e, index, type) => {
    const refs =
      type === 'mobile' ? mobileRefs : emailRefs;

    if (
      e.key === 'Backspace' &&
      !e.target.value &&
      index > 0
    ) {
      refs.current[index - 1]?.focus();
    }
  };

  /*
   * Timer
   */
  useEffect(() => {
    if (!isOpen) return;

    if (isMobileVerified) {
      setMobileTimer(0);
    } else {
      setMobileTimer(30);
    }

    if (isEmailVerified) {
      setEmailTimer(0);
    } else {
      setEmailTimer(30);
    }

    const interval = setInterval(() => {
      setMobileTimer((prev) =>
        prev > 0 ? prev - 1 : 0
      );

      setEmailTimer((prev) =>
        prev > 0 ? prev - 1 : 0
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, isMobileVerified, isEmailVerified]);

  /*
   * Don't render modal
   */
  if (!isOpen) return null;

  /*
   * Resend OTP
   */
  const resendOtp = async (type) => {
    try {
      if (!registrationFormData) {
        Toaster.error(
          t('sessionExpired')
        );
        return;
      }

      if (
        !registrationFormData?.mobile_number ||
        !registrationFormData?.country_code ||
        !registrationFormData?.email
      ) {
        Toaster.error(
          t('incompleteRegistrationData')
        );
        return;
      }

      const payload = {
        type,
        mobile_number:
          registrationFormData.mobile_number,
        country_code:
          registrationFormData.country_code,
        email: registrationFormData.email,
      };

      setIsResending(type);

      const res = await dispatch(
        resendOTP(payload)
      ).unwrap();

      if (res?.status) {
        Toaster.success(
          res?.message ||
            t('otpResent', {
              type:
                type === 'phone'
                  ? t('mobile')
                  : t('email'),
            })
        );

        if (type === 'phone') {
          setMobileTimer(30);
          setMobileOtp(new Array(4).fill(''));
          mobileRefs.current[0]?.focus();
        } else {
          setEmailTimer(30);
          setEmailOtp(new Array(4).fill(''));
          emailRefs.current[0]?.focus();
        }
      } else {
        Toaster.error(
          res?.message ||
            t('resendOtpFailed')
        );
      }
    } catch (err) {
      Toaster.error(
        err?.message ||
          t('resendOtpError')
      );
    } finally {
      setIsResending(false);
    }
  };

  /*
   * Verify OTP
   */
  const verifyEnteredOTP = async (type) => {
    try {
      if (!registrationFormData) {
        Toaster.error(
          t('sessionExpired')
        );
        return;
      }

      if (
        !registrationFormData?.mobile_number ||
        !registrationFormData?.country_code ||
        !registrationFormData?.email
      ) {
        Toaster.error(
          t('incompleteRegistrationData')
        );
        return;
      }

      const otpArray =
        type === 'phone'
          ? mobileOtp
          : emailOtp;

      /*
       * Complete OTP validation
       */
      if (
        otpArray.some(
          (digit) => digit === ''
        )
      ) {
        Toaster.error(
          t('completeOtp')
        );
        return;
      }

      const otp = otpArray.join('');

      /*
       * Invalid OTP validation
       */
      if (
        otp.length !== 4 ||
        !/^\d{4}$/.test(otp)
      ) {
        Toaster.error(
          t('invalidOtp')
        );
        return;
      }

      const payload = {
        type,
        otp,
        mobile_number:
          registrationFormData?.mobile_number,
        country_code:
          registrationFormData?.country_code,
        email:
          registrationFormData?.email,
      };

      setIsVerifying(type);

      const res = await dispatch(
        verifyOTP(payload)
      ).unwrap();

      if (res?.status) {
        Toaster.success(
          res?.message ||
            t('otpVerified', {
              type:
                type === 'phone'
                  ? t('mobileNumber')
                  : t('email'),
            })
        );

        if (type === 'phone') {
          localStorage.setItem(
            'is_mobile_verified',
            'true'
          );
        } else {
          localStorage.setItem(
            'is_email_verified',
            'true'
          );
        }

        const mobileNow =
          type === 'phone'
            ? 'true'
            : localStorage.getItem(
                'is_mobile_verified'
              );

        const emailNow =
          type === 'email'
            ? 'true'
            : localStorage.getItem(
                'is_email_verified'
              );

        if (
          mobileNow === 'true' &&
          emailNow === 'true'
        ) {
          submitVerifiedRegistration();
        }
      } else {
        Toaster.error(
          res?.message ||
            t('invalidOtpTryAgain')
        );

        resetOtp(type);

        setIsVerifying(false);
      }
    } catch (err) {
      Toaster.error(
        err?.message ||
          t('verifyOtpError')
      );

      resetOtp(type);

      setIsVerifying(false);
    } finally {
      setIsVerifying(false);
    }
  };

  /*
   * Reset OTP fields
   */
  const resetOtp = (type) => {
    if (type === 'phone') {
      setMobileOtp(new Array(4).fill(''));
      mobileRefs.current[0]?.focus();
    } else {
      setEmailOtp(new Array(4).fill(''));
      emailRefs.current[0]?.focus();
    }
  };

  /*
   * Complete registration
   */
  const submitVerifiedRegistration = async () => {
    try {
      const res = await dispatch(
        completeRegistration(
          registrationFormData
        )
      ).unwrap();

      if (res?.status) {
        onClose();
        onSuccess();
      } else {
        Toaster.error(
          res?.message ||
            t('registrationFailed')
        );
      }
    } catch (err) {
      Toaster.error(
        err?.message ||
          t('registrationError')
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {loading && <ShowLoader />}

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#2F302C99] backdrop-blur-[1px]"
        onClick={onClose}
      ></div>

      <div className="relative min-h-screen flex items-start sm:items-center justify-center p-4">
        <div
          className="bg-white rounded-[1rem] w-full max-w-3xl relative p-6 sm:p-10 my-8 
          max-h-[90vh] overflow-y-auto
          transform transition-all animate-in fade-in zoom-in duration-300 shadow-xl z-10"
        >
          {/* Background Watermark */}
          <div
            className="absolute inset-0 z-0 opacity-5 pointer-events-none flex items-center justify-center"
            style={{
              backgroundImage:
                `url('/img/logos/logo.png')`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: '90%',
            }}
          ></div>

          <div className="relative z-10">
            {/* Close Button */}
            <button
              onClick={onClose}
              aria-label={t('close')}
              className="absolute top-[-20px] right-[-20px] text-[#FF0000] hover:rotate-90 transition-all duration-300 ring-2 ring-[#FF0000] rounded-3xl bg-white p-1"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>

            {/* Header */}
            <div className="flex flex-col items-center mb-8">
              <div className="w-24 h-24 bg-[#E6EFEB] rounded-full flex items-center justify-center mb-6">
                <span className="text-3xl">
                  <img
                    src="/img/icons/Group-119.png"
                    height={14}
                    width={41}
                    alt={t('otpIcon')}
                  />
                </span>
              </div>

              <h2 className="text-2xl font-black text-[#2F302C] tracking-tight">
                {t('otpTitle')}
              </h2>
            </div>

            {/* ================= MOBILE OTP ================= */}
            <div className="space-y-5 mb-8">
              <p className="text-center text-sm text-[#2F302CDE]">
                {t('otpDescription', {
                  type: t('mobileNumber'),
                })}
              </p>

              <div className="flex justify-center gap-5">
                {mobileOtp.map(
                  (data, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength="1"
                      inputMode="numeric"
                      ref={(el) =>
                        (mobileRefs.current[index] =
                          el)
                      }
                      value={data}
                      onChange={(e) =>
                        handleChange(
                          e.target,
                          index,
                          'mobile'
                        )
                      }
                      onKeyDown={(e) =>
                        handleKeyDown(
                          e,
                          index,
                          'mobile'
                        )
                      }
                      aria-label={`${t(
                        'mobileNumber'
                      )} ${t('otpDigit')} ${
                        index + 1
                      }`}
                      className="w-12 h-12 border-2 border-[#CFD2D9] rounded-xl text-center text-2xl font-bold focus:border-amber-500 focus:ring-4 focus:ring-amber-100 focus:outline-none transition-all"
                      disabled={
                        isMobileVerified
                      }
                    />
                  )
                )}
              </div>

              {/* Mobile Resend */}
              {!isMobileVerified && (
                <div className="text-center text-sm text-[#384042] flex items-center justify-center">
                  {mobileTimer > 0 ? (
                    <span>
                      {t('resendOtpIn', {
                        seconds:
                          mobileTimer,
                      })}
                    </span>
                  ) : (
                    <button
                      className="relative text-amber-600 hover:bg-amber-50 px-3 py-1.5 rounded-md font-medium transition-all"
                      onClick={() =>
                        resendOtp('phone')
                      }
                      disabled={
                        isResending
                      }
                    >
                      {isResending ===
                      'phone'
                        ? t('resending')
                        : t('resendOtp')}
                    </button>
                  )}
                </div>
              )}

              {/* Mobile Verify */}
              <div className="flex flex-col items-center w-full">
                <button
                  className={`relative w-80 py-4 rounded-2xl font-bold transition-all duration-300 flex items-center justify-center overflow-hidden
                    ${
                      isMobileVerified
                        ? 'bg-green-500 text-white cursor-not-allowed'
                        : 'bg-[#F4B11F] hover:bg-amber-600 text-white shadow-md active:scale-[0.97]'
                    }`}
                  disabled={
                    isMobileVerified ||
                    isVerifying === 'phone'
                  }
                  onClick={() =>
                    verifyEnteredOTP(
                      'phone'
                    )
                  }
                >
                  <span
                    className={`transition-opacity duration-200 ${
                      isVerifying ===
                      'phone'
                        ? 'opacity-0'
                        : 'opacity-100'
                    }`}
                  >
                    {isMobileVerified
                      ? t('verified')
                      : t('verifyOtp')}
                  </span>

                  {isVerifying ===
                    'phone' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="animate-spin h-6 w-6 text-white"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>

                        <path
                          className="opacity-100"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  )}
                </button>
              </div>
            </div>

            {/* ================= EMAIL OTP ================= */}
            <div className="space-y-5">
              <p className="text-center text-sm text-[#2F302CDE]">
                {t('otpDescription', {
                  type: t('email'),
                })}
              </p>

              <div className="flex justify-center gap-5">
                {emailOtp.map(
                  (data, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength="1"
                      inputMode="numeric"
                      ref={(el) =>
                        (emailRefs.current[index] =
                          el)
                      }
                      value={data}
                      onChange={(e) =>
                        handleChange(
                          e.target,
                          index,
                          'email'
                        )
                      }
                      onKeyDown={(e) =>
                        handleKeyDown(
                          e,
                          index,
                          'email'
                        )
                      }
                      aria-label={`${t(
                        'email'
                      )} ${t('otpDigit')} ${
                        index + 1
                      }`}
                      className="w-12 h-12 border-2 border-[#CFD2D9] rounded-xl text-center text-2xl font-bold focus:border-amber-500 focus:ring-4 focus:ring-amber-100 focus:outline-none transition-all"
                      disabled={
                        isEmailVerified
                      }
                    />
                  )
                )}
              </div>

              {/* Email Resend */}
              {!isEmailVerified && (
                <div className="text-center text-sm text-[#384042] flex items-center justify-center mt-2">
                  {emailTimer > 0 ? (
                    <span>
                      {t('resendOtpIn', {
                        seconds:
                          emailTimer,
                      })}
                    </span>
                  ) : (
                    <button
                      className="relative text-amber-600 hover:bg-amber-50 px-3 py-1.5 rounded-md font-medium transition-all"
                      onClick={() =>
                        resendOtp('email')
                      }
                      disabled={
                        isResending
                      }
                    >
                      {isResending ===
                      'email'
                        ? t('resending')
                        : t('resendOtp')}
                    </button>
                  )}
                </div>
              )}

              {/* Email Verify */}
              <div className="flex flex-col items-center w-full mt-5">
                <button
                  className={`relative w-80 py-4 rounded-2xl font-bold transition-all duration-300 flex items-center justify-center overflow-hidden
                    ${
                      isEmailVerified
                        ? 'bg-green-500 text-white cursor-not-allowed'
                        : 'bg-[#F4B11F] hover:bg-amber-600 text-white shadow-md active:scale-[0.97]'
                    }`}
                  disabled={
                    isEmailVerified ||
                    isVerifying === 'email'
                  }
                  onClick={() =>
                    verifyEnteredOTP(
                      'email'
                    )
                  }
                >
                  <span
                    className={`transition-opacity duration-200 ${
                      isVerifying ===
                      'email'
                        ? 'opacity-0'
                        : 'opacity-100'
                    }`}
                  >
                    {isEmailVerified
                      ? t('verified')
                      : t('verifyOtp')}
                  </span>

                  {isVerifying ===
                    'email' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="animate-spin h-6 w-6 text-white"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>

                        <path
                          className="opacity-100"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OTPVerification;